import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Input, Modal, Radio, Tag, Typography } from "antd";
import { IBrandFieldSuggestions } from "../../services/nestjs-base-api";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

// Fixed display order; only fields present in `suggestions` are shown.
const FIELD_ORDER: { key: keyof IBrandFieldSuggestions; label: string }[] = [
    { key: "positioning", label: "Positioning" },
    { key: "productsServices", label: "Products & Services" },
    { key: "targetCustomers", label: "Target Customers" },
    { key: "keyCompetitors", label: "Key Competitors" },
    { key: "toneOfVoice", label: "Tone of Voice" },
    { key: "brandGoals", label: "Brand Goals" },
];

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();

/** True when the suggestion is essentially already present in the field. */
const isCovered = (existing: string, suggestion: string) => {
    const e = norm(existing);
    const s = norm(suggestion);
    return !!e && !!s && (e.includes(s) || s.includes(e));
};

interface IProps {
    open: boolean;
    suggestions: IBrandFieldSuggestions;
    currentValues: Record<string, string>;
    onApply: (accepted: Partial<Record<string, string>>) => void;
    onClose: () => void;
}

interface RowState {
    include: boolean;
    text: string;
    mode: "append" | "replace";
}

/**
 * SYR-78 — review AI field suggestions. All suggested fields at once; each can be
 * appended or replaced (already-filled fields default to Replace to avoid
 * duplication), and suggestions already covered by the field are auto-skipped.
 */
const FieldSuggestionsModal: React.FC<IProps> = ({
    open,
    suggestions,
    currentValues,
    onApply,
    onClose,
}) => {
    const fields = useMemo(
        () => FIELD_ORDER.filter((f) => !!suggestions[f.key]),
        [suggestions]
    );

    const [rows, setRows] = useState<Record<string, RowState>>({});

    useEffect(() => {
        if (open) {
            const initial: Record<string, RowState> = {};
            fields.forEach((f) => {
                const key = f.key as string;
                const existing = currentValues[key] || "";
                const text = suggestions[f.key] || "";
                const covered = isCovered(existing, text);
                initial[key] = {
                    // Already-covered suggestions start unticked (nothing to add).
                    include: !covered,
                    text,
                    // Filled fields default to Replace so we never duplicate.
                    mode: existing.trim() ? "replace" : "append",
                };
            });
            setRows(initial);
        }
    }, [open, fields, suggestions, currentValues]);

    if (!open || fields.length === 0) return null;

    const selectedCount = fields.filter(
        (f) => rows[f.key as string]?.include && rows[f.key as string]?.text.trim()
    ).length;

    const update = (key: string, patch: Partial<RowState>) =>
        setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const handleApply = () => {
        const accepted: Record<string, string> = {};
        fields.forEach((f) => {
            const key = f.key as string;
            const row = rows[key];
            if (!row?.include || !row.text.trim()) return;
            const existing = (currentValues[key] || "").trim();
            accepted[key] =
                existing && row.mode === "append"
                    ? `${existing}\n\n${row.text.trim()}`
                    : row.text.trim();
        });
        onApply(accepted);
        onClose();
    };

    return (
        <Modal
            open={open}
            title="Add to your brand knowledge"
            onCancel={onClose}
            width={660}
            okText={
                selectedCount > 0
                    ? `Apply ${selectedCount} to fields`
                    : "Apply to fields"
            }
            okButtonProps={{ disabled: selectedCount === 0 }}
            onOk={handleApply}
        >
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
                We pulled these from your uploaded content. Untick anything you
                don't want, tweak the wording, and choose whether to append to or
                replace each field.
            </Paragraph>

            <div style={{ maxHeight: "55vh", overflow: "auto", paddingRight: 4 }}>
                {fields.map((f) => {
                    const key = f.key as string;
                    const row = rows[key] || {
                        include: true,
                        text: "",
                        mode: "append" as const,
                    };
                    const existing = (currentValues[key] || "").trim();
                    const covered = isCovered(existing, row.text);
                    return (
                        <div
                            key={key}
                            style={{
                                padding: "12px 0",
                                borderBottom: "1px solid #f0f0f0",
                                opacity: row.include ? 1 : 0.5,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <Checkbox
                                    checked={row.include}
                                    onChange={(e) =>
                                        update(key, { include: e.target.checked })
                                    }
                                >
                                    <Text strong>{f.label}</Text>
                                </Checkbox>
                                {covered ? (
                                    <Tag color="default">already in field</Tag>
                                ) : !existing ? (
                                    <Tag>fills empty</Tag>
                                ) : null}
                                {existing && (
                                    <Radio.Group
                                        size="small"
                                        optionType="button"
                                        buttonStyle="solid"
                                        value={row.mode}
                                        onChange={(e) =>
                                            update(key, {
                                                mode: e.target.value as
                                                    | "append"
                                                    | "replace",
                                            })
                                        }
                                        options={[
                                            { label: "Append", value: "append" },
                                            { label: "Replace", value: "replace" },
                                        ]}
                                        style={{ marginLeft: "auto" }}
                                    />
                                )}
                            </div>
                            <TextArea
                                value={row.text}
                                onChange={(e) => update(key, { text: e.target.value })}
                                disabled={!row.include}
                                autoSize={{ minRows: 2, maxRows: 8 }}
                            />
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default FieldSuggestionsModal;
